import { Check, X, AlertTriangle } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { ScrubInput, NumericDisplay } from 'calc-controls';
import { useStore } from '@/store';
import {
  selectPkParameters,
  selectBioequivalence,
  selectResult,
  selectArms,
} from '@/store/selectors';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { Tooltip } from '@/components/ui/Tooltip';
import { ScreenEmpty } from '@/components/ui/ScreenEmpty';
import { formatNumber, formatPkRow } from '@/utils/format';
import { CopyButton } from '@/components/calculator/CopyButton';
import { isPhase3Enabled } from '@/config/features';
import type { BcsClass } from '@bioavail/shared-types';

/** Food-effect × BCS classification interpretation (F-073). */
function foodEffectBcsInterpretation(aucRatio: number, bcsClass: BcsClass | undefined): string {
  if (bcsClass === undefined) {
    return 'Set a BCS class (StudyModeBar) to contextualize this food effect.';
  }
  const lowSolubility = bcsClass === 'II' || bcsClass === 'IV';
  if (lowSolubility && aucRatio > 1) {
    return `Consistent with positive food effect typical of low-solubility (BCS Class ${bcsClass}) drugs.`;
  }
  const highSolubility = bcsClass === 'I' || bcsClass === 'III';
  if (highSolubility && aucRatio > 1.2) {
    return `Unexpected for a high-solubility (BCS Class ${bcsClass}) drug — verify study conditions.`;
  }
  return `BCS Class ${bcsClass} recorded; food-effect magnitude outside typical expectation — review.`;
}

/** Complementary below-threshold signal (delta remediation). Returns null
 *  when no positive (i.e. "no clinically meaningful food effect") message
 *  applies — typically when the ratio is within ±20% and a BCS class is set. */
function foodEffectBelowThresholdMessage(
  aucRatio: number,
  cmaxRatio: number,
  bcsClass: BcsClass | undefined,
): string | null {
  if (bcsClass === undefined) return null;
  const withinWindow = Math.abs(aucRatio - 1) <= 0.2 && Math.abs(cmaxRatio - 1) <= 0.2;
  if (!withinWindow) return null;
  const highSolubility = bcsClass === 'I' || bcsClass === 'III';
  if (highSolubility) {
    return `No clinically meaningful food effect (AUC/Cmax within ±20%); consistent with the high solubility of BCS Class ${bcsClass}.`;
  }
  return `AUC/Cmax within ±20% — no clinically meaningful food effect detected for BCS Class ${bcsClass}.`;
}

/**
 * Analysis screen — secondary PK parameters, BE assessment, sensitivity,
 * and advanced analytics (single-column stacked sections).
 */
export function AnalysisScreen() {
  const result = useStore(selectResult);
  const arms = useStore(useShallow(selectArms));
  const setActiveScreen = useStore((s) => s.setActiveScreen);
  const requestLoadDemo = useStore((s) => s.requestLoadDemo);

  if (!result) {
    return (
      <ScreenEmpty
        title="No result yet"
        body="Compute a result on the Calculator to see secondary PK parameters and bioequivalence assessment."
        primary={{ label: 'Open Calculator', onClick: () => setActiveScreen('calculator') }}
        secondary={{ label: 'Load Demo', onClick: requestLoadDemo }}
      />
    );
  }

  return (
    <div className="analysis-screen">
      <PkParametersSection arms={arms} />
      <BioequivalenceSection />
      <SensitivitySection />
      <AdvancedSection />
    </div>
  );
}

function PkParametersSection({ arms }: { arms: ReturnType<typeof selectArms> }) {
  const pkParams = useStore(useShallow(selectPkParameters));
  const sigFigs = useStore((s) => s.settings.significantFigures);

  return (
    <CollapsibleSection sectionKey="analysis.pkParams" title="Secondary PK Parameters" column="single">
      {pkParams.length === 0 ? (
        <p className="empty-state">No PK parameters computed yet.</p>
      ) : (
        <>
          <p className="field-hint">
            Cmax/Tmax from observed peaks. t½ = ln(2)/λ_z. CL/F = Dose/AUC. Vz/F = CL/F ÷ λ_z.
            MRT/MAT require an IV arm. Units follow each arm&apos;s concentration and time basis.
          </p>
          <div className="table-scroll">
          <table className="generic-table">
            <thead>
              <tr>
                <th>Arm</th>
                <th>
                  Cmax
                  <Tooltip label="Cmax">Peak observed concentration (earliest on tie), in the arm&apos;s concentration unit.</Tooltip>
                </th>
                <th>
                  Tmax
                  <Tooltip label="Tmax">Time of Cmax, in the arm&apos;s time unit.</Tooltip>
                </th>
                <th>
                  t½
                  <Tooltip label="t½">Elimination half-life = ln(2)/λ_z (h). Requires AUC(0-∞).</Tooltip>
                </th>
                <th>
                  CL/F
                  <Tooltip label="CL/F">Apparent clearance = Dose ÷ AUC (mg per concentration·time). Null without λ_z.</Tooltip>
                </th>
                <th>
                  Vz/F
                  <Tooltip label="Vz/F">Apparent volume = CL/F ÷ λ_z. Null without λ_z.</Tooltip>
                </th>
                <th>
                  MRT
                  <Tooltip label="MRT">Mean residence time = AUMC/AUC (h).</Tooltip>
                </th>
                <th>
                  MAT
                  <Tooltip label="MAT">Mean absorption time = MRT_oral − MRT_IV (h). Only with an IV arm.</Tooltip>
                </th>
                <th>Copy</th>
              </tr>
            </thead>
            <tbody>
              {pkParams.map((pk) => {
                const arm = arms.find((a) => a.id === pk.armId);
                return (
                  <tr key={pk.armId}>
                    <td>{arm?.label ?? pk.armId}</td>
                    <td className="mono">{formatNumber(pk.cmax, sigFigs)}</td>
                    <td className="mono">{formatNumber(pk.tmax, sigFigs)}</td>
                    <td className="mono">{pk.halfLife !== null ? formatNumber(pk.halfLife, sigFigs) : '—'}</td>
                    <td className="mono">{pk.clOverF !== null ? formatNumber(pk.clOverF, sigFigs) : '—'}</td>
                    <td className="mono">{pk.vzOverF !== null ? formatNumber(pk.vzOverF, sigFigs) : '—'}</td>
                    <td className="mono">{pk.mrt ? formatNumber(pk.mrt, sigFigs) : '—'}</td>
                    <td className="mono">
                      <span className="stat-secondary-value">
                        {pk.mat ? formatNumber(pk.mat, sigFigs) : '—'}
                      </span>
                      {pk.mat !== undefined && pk.halfLife !== null && pk.halfLife > 0 ? (
                        <span
                          className={
                            pk.mat / pk.halfLife > 1
                              ? 'inline-badge'
                              : 'inline-badge inline-badge--neutral'
                          }
                        >
                          {' '}MAT/t½={(pk.mat / pk.halfLife).toFixed(2)}
                          {pk.mat / pk.halfLife > 1 ? ' · flip-flop?' : ''}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <CopyButton
                        label=""
                        accessibleName={`Copy PK row for ${arm?.label ?? pk.armId}`}
                        getText={() => formatPkRow(arm?.label ?? pk.armId, pk)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </CollapsibleSection>
  );
}

function BioequivalenceSection() {
  const be = useStore(selectBioequivalence);
  const sigFigs = useStore((s) => s.settings.significantFigures);
  const study = useStore((s) => s.studies.find((x) => x.id === s.activeStudyId));
  const requestLoadDemo = useStore((s) => s.requestLoadDemo);

  return (
    <CollapsibleSection sectionKey="analysis.bioequivalence" title="Bioequivalence Assessment" column="single">
      {!be ? (
        <ScreenEmpty
          body="Bioequivalence assessment requires a multi-subject dataset (Phase 2). Upload data on the Studies screen to enable TOST 90% CI evaluation."
          primary={{ label: 'Load Demo', onClick: requestLoadDemo }}
        />
      ) : (
        <div className="flex-col-gap-2">
          <p className="field-hint">
            Two one-sided t-tests (TOST) on log-transformed T/R ratios. The 90% CI must
            fall within 80–125% for both AUC and Cmax to declare bioequivalence.
          </p>
          <div className="flex-items-center-gap-2-wrap">
            <div className={`be-verdict ${be.overallPass ? 'pass' : 'fail'}`}>
              {be.overallPass ? (
                <>
                  <Check size={16} aria-hidden="true" /> Bioequivalent
                </>
              ) : (
                <>
                  <X size={16} aria-hidden="true" /> Not bioequivalent
                </>
              )}
            </div>
            <CopyButton
              label=""
              accessibleName="Copy BE verdict"
              getText={() =>
                `${be.overallPass ? 'Bioequivalent' : 'Not bioequivalent'}: ` +
                `AUC GMR ${(be.aucGmr * 100).toFixed(1)}% [${(be.aucCI90.lower * 100).toFixed(1)}–${(be.aucCI90.upper * 100).toFixed(1)}], ` +
                `Cmax GMR ${(be.cmaxGmr * 100).toFixed(1)}% [${(be.cmaxCI90.lower * 100).toFixed(1)}–${(be.cmaxCI90.upper * 100).toFixed(1)}]`
              }
            />
          </div>
          <div className="table-scroll">
            <table className="generic-table">
              <thead>
                <tr>
                  <th>Endpoint</th>
                  <th>GMR</th>
                  <th>90% CI Lower</th>
                  <th>90% CI Upper</th>
                  <th>Verdict</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>AUC</td>
                  <td className="mono">{formatNumber(be.aucGmr * 100, sigFigs)}%</td>
                  <td className="mono">{formatNumber(be.aucCI90.lower * 100, sigFigs)}%</td>
                  <td className="mono">{formatNumber(be.aucCI90.upper * 100, sigFigs)}%</td>
                  <td>{be.aucPass ? <Check size={14} aria-hidden="true" /> : <X size={14} aria-hidden="true" />}</td>
                </tr>
                <tr>
                  <td>Cmax</td>
                  <td className="mono">{formatNumber(be.cmaxGmr * 100, sigFigs)}%</td>
                  <td className="mono">{formatNumber(be.cmaxCI90.lower * 100, sigFigs)}%</td>
                  <td className="mono">{formatNumber(be.cmaxCI90.upper * 100, sigFigs)}%</td>
                  <td>{be.cmaxPass ? <Check size={14} aria-hidden="true" /> : <X size={14} aria-hidden="true" />}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {be.isHvd ? (
            <p className="field-hint"><AlertTriangle size={14} aria-hidden="true" /> Highly variable drug detected (CV &gt; 30%). RSABE may apply.</p>
          ) : null}
          {be.foodEffectRatios ? (
            <div className="food-effect">
              <span className="field-label">Food-effect ratios:</span>
              <span className="mono">
                AUC ratio={formatNumber(be.foodEffectRatios.aucRatio, sigFigs)} ·
                Cmax ratio={formatNumber(be.foodEffectRatios.cmaxRatio, sigFigs)}
                {be.foodEffectRatios.exceedsTwentyPercent ? (
                  <>
                    {' '}
                    <AlertTriangle size={14} aria-hidden="true" /> exceeds ±20%
                  </>
                ) : ''}
              </span>
              {be.foodEffectRatios.exceedsTwentyPercent ? (
                <p className="field-hint">{foodEffectBcsInterpretation(be.foodEffectRatios.aucRatio, study?.bcsClass)}</p>
              ) : (
                (() => {
                  const msg = foodEffectBelowThresholdMessage(
                    be.foodEffectRatios.aucRatio,
                    be.foodEffectRatios.cmaxRatio,
                    study?.bcsClass,
                  );
                  return msg ? <p className="field-hint">{msg}</p> : null;
                })()
              )}
            </div>
          ) : null}
        </div>
      )}
    </CollapsibleSection>
  );
}

function SensitivitySection() {
  const study = useStore((s) => s.studies.find((x) => x.id === s.activeStudyId));
  const updateArm = useStore((s) => s.updateArm);
  const result = useStore(selectResult);

  if (!study || !result) {
    return (
      <CollapsibleSection sectionKey="analysis.sensitivity" title="Sensitivity / What-if" column="single">
        <p className="empty-state">No active result.</p>
      </CollapsibleSection>
    );
  }

  const testArm = study.arms.find((a) => a.role === 'test');
  const refArm = study.arms.find((a) => a.role === 'reference');

  return (
    <CollapsibleSection sectionKey="analysis.sensitivity" title="Sensitivity / What-if" column="single">
      <p className="field-hint">Adjust doses to see how F changes.</p>
      <div className="flex-items-baseline-gap-2">
        <span className="field-label mb-0">Current F</span>
        <NumericDisplay value={result.fPercent} unit="%" decimals={4} inline />
      </div>
      <div className="flex-row-gap-3-wrap">
        {testArm ? (
          <div className="number-input-wrapper">
            <label className="field-label">Test dose ({testArm.doseUnit})</label>
            <ScrubInput
              label={`Test dose (${testArm.doseUnit})`}
              value={testArm.dose}
              onChange={(v) => updateArm(testArm.id, { dose: v })}
              min={0}
              decimals={4}
              unit={testArm.doseUnit}
              inline
            />
          </div>
        ) : null}
        {refArm ? (
          <div className="number-input-wrapper">
            <label className="field-label">Reference dose ({refArm.doseUnit})</label>
            <ScrubInput
              label={`Reference dose (${refArm.doseUnit})`}
              value={refArm.dose}
              onChange={(v) => updateArm(refArm.id, { dose: v })}
              min={0}
              decimals={4}
              unit={refArm.doseUnit}
              inline
            />
          </div>
        ) : null}
      </div>
    </CollapsibleSection>
  );
}

function AdvancedSection() {
  if (!isPhase3Enabled()) {
    return (
      <CollapsibleSection sectionKey="analysis.advanced" title="Advanced Analytics" column="single">
        <p className="empty-state">
          Advanced analytics (dose-dependence modeling, contribution analysis, external model integration)
          are available in Phase 3. Set <code>VITE_FEATURE_PHASE3=true</code> to enable.
        </p>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection sectionKey="analysis.advanced" title="Advanced Analytics" column="single">
      <div className="flex-col-gap-3">
        <div>
          <h4 className="field-label">Dose-dependence (F-063)</h4>
          <p className="field-hint">Assess whether F is dose-proportional across available dose levels.</p>
        </div>
        <div>
          <h4 className="field-label">Contribution analysis (F-064)</h4>
          <p className="field-hint">Decompose the relative contribution of absorption vs disposition to overall F.</p>
        </div>
        <div>
          <h4 className="field-label">External model integration (F-065)</h4>
          <p className="field-hint">Accept externally fitted PK parameters (e.g. from NLME/Winnonlin).</p>
        </div>
      </div>
    </CollapsibleSection>
  );
}
